<template>
  <div class="mb-6">
    <Card>
      <CardHeader>
        <CardTitle>Upcoming</CardTitle>
        <CardAction>
          <Button variant="card-header-action" size="sm" @click="showScheduleMeeting = true">Schedule Meeting</Button>
        </CardAction>
      </CardHeader>
      <CardContent class="pb-6">
        <ul v-if="activities.length > 0" role="list">
          <li
            v-for="(item, index) of activities.slice(0, notesIndex)"
            :key="`${item.category}-${item.id ?? index}`"
            class="last:pb-2 [&:last-child_.timeline-line]:hidden"
          >
            <ContactActivityPreview
              :item="item"
              @delete-interview-clicked="cancelInterviewCandidate = item"
              @cancel-workflow-clicked="cancelWorkflowItem = item"
              @cancel-scheduled-action-clicked="cancelScheduledActionItem = item"
            />
          </li>
        </ul>
        <div
          v-if="activities.length === 0"
          class="flex min-h-28 items-center justify-center rounded-lg border border-dashed border-gray-200 bg-gray-50/40 px-6 text-center text-sm text-gray-500"
        >
          No upcoming activities found
        </div>
        <Button
          v-if="activities.length > notesIndex"
          variant="ghost"
          class="px-2 text-gray-500"
          @click="notesIndex = notesIndex + 999999"
        >
          Show All
        </Button>
        <Button v-else-if="activities.length > 5" variant="ghost" class="px-2 text-gray-500" @click="notesIndex = 5">
          Show Less
        </Button>
      </CardContent>
    </Card>

    <ScheduleMeeting
      v-if="showScheduleMeeting"
      :contact="contact"
      @close="showScheduleMeeting = false"
      @scheduled="
        showScheduleMeeting = false;
        emits('data-changed');
      "
    />

    <Dialog
      :open="!!cancelInterviewCandidate"
      @update:open="
        val => {
          if (!val) cancelInterviewCandidate = null;
        }
      "
    >
      <DialogContent class="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Delete Interview</DialogTitle>
          <DialogDescription>
            Are you sure you want to delete {{ contact.name }}'s
            {{ cancelInterviewCandidate ? noteDetails(cancelInterviewCandidate) : '' }}. This action will cancel the
            {{ cancelInterviewCandidate?.label }}.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <DialogClose as-child>
            <Button variant="outline">Cancel</Button>
          </DialogClose>
          <Button variant="destructive" @click="cancelEvent(cancelInterviewCandidate!.id)">Delete</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <Dialog
      :open="!!cancelWorkflowItem"
      @update:open="
        val => {
          if (!val) cancelWorkflowItem = null;
        }
      "
    >
      <DialogContent class="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Cancel Workflow</DialogTitle>
          <DialogDescription> Are you sure you want to cancel this {{ cancelWorkflowItem?.label }}? </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <DialogClose as-child>
            <Button variant="outline">No, keep it</Button>
          </DialogClose>
          <Button variant="destructive" @click="cancelWorkflow(cancelWorkflowItem!)">Cancel Workflow</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <Dialog
      :open="!!cancelScheduledActionItem"
      @update:open="
        val => {
          if (!val) cancelScheduledActionItem = null;
        }
      "
    >
      <DialogContent class="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Cancel Action</DialogTitle>
          <DialogDescription>
            Are you sure you want to cancel this {{ cancelScheduledActionItem?.label }}?
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <DialogClose as-child>
            <Button variant="outline">No, keep it</Button>
          </DialogClose>
          <Button variant="destructive" @click="cancelScheduledAction(cancelScheduledActionItem!)">
            Cancel {{ cancelScheduledActionItem?.label }}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { Card, CardAction, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import Button from '../ui/button/Button.vue';
import ContactActivityPreview from './ContactActivity/ContactActivityPreview.vue';
import type { ContactDetail } from './ContactDetail';
import type { ActivityItem } from './ContactActivity/Transformers/useActivityTransformer';
import { Api } from '@/api';
import { toast } from 'vue-sonner';
import { useLoadingSpinnerStore } from '@/stores/loadingSpinner';
import ScheduleMeeting from './ScheduleMeetingSheet/index.vue';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from '@/components/ui/dialog';

const emits = defineEmits<{ 'data-changed': [] }>();

defineProps<{
  contact: ContactDetail;
  activities: ActivityItem[];
}>();

const showScheduleMeeting = ref(false);
const cancelInterviewCandidate = ref<ActivityItem | null>(null);
const cancelWorkflowItem = ref<ActivityItem | null>(null);
const cancelScheduledActionItem = ref<ActivityItem | null>(null);
const loadingSpinnerStore = useLoadingSpinnerStore();

const notesIndex = ref(5);

const cancelEvent = async (id: number) => {
  const response = await Api.ContactEvents.deleteContactEvent(id);
  loadingSpinnerStore.hideLocalLoadingSpinner();

  if (response.status === 'error') {
    toast.error('Interview Deletion Failed', { description: response.messages.join(', ') });
  } else {
    cancelInterviewCandidate.value = null;
    toast.success('Interview Deletion Successful', {
      description: 'Your interview was successfully deleted.',
    });
    emits('data-changed');
  }
};

const cancelScheduledAction = async (item: ActivityItem) => {
  const response = await Api.ScheduledActions.cancelScheduledAction(item.id);
  if (response?.status === 'error') {
    toast.error('Activity delete error', {
      description: response.messages,
    });
  } else {
    cancelScheduledActionItem.value = null;
    toast.success('Activity deleted', {
      description: 'Your activity was successfully deleted.',
    });
    emits('data-changed');
  }
};

const cancelWorkflow = async (item: ActivityItem) => {
  const response = await Api.WorkflowScheduledActionInstances.updateWorkflowScheduledActionInstance({
    id: item.id,
    status: 'canceled',
  });

  if (response.status === 'error') {
    toast.error('Cancellation Failed', { description: response.messages?.join(', ') });
  } else {
    cancelWorkflowItem.value = null;
    toast.success('Workflow Cancelled', { description: `${item.label} has been cancelled.` });
    emits('data-changed');
  }
};

const noteDetails = (activity: ActivityItem) => {
  const event = activity.content?.find(content => content.label === activity.label);
  if (event?.label && event?.richText) {
    return event.label + ' - ' + event.richText;
  }
  return activity.label;
};
</script>
